// La ficha de un producto que ya está en Tiendanube, armada con lo que hoy está publicado
// (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento). La regla de qué va en cada
// campo es pura (`fichaDesdeLaTienda`); acá se escribe.
//
// Dos caminos:
//   · al SUMAR un producto que ya está en la tienda a un lanzamiento (`completarConLaTienda`): la
//     ficha arranca igual a la tienda, así lo que después se marca como cambio es sólo lo que tocó la
//     persona. Antes arrancaba con los datos del catálogo de Atlas y todo hubiera aparecido "cambiado".
//   · "Traer lo de la tienda" de la ficha (`traerLoDeLaTienda`): vuelve a ponerla como la tienda, para
//     los productos sumados antes de esto o para deshacer todo.
//
// Los dos traban todas las secciones si el lanzamiento va sólo a Tiendanube
// (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas): la ficha queda igual a
// la tienda y nada se manda hasta que la persona destrabe una sección. Con otra tienda en el lanzamiento
// (Mercado Libre) no se traba nada: allá el producto se crea con la ficha entera.

import { leerPublicadosEnTn } from "../../lib/tiendaNube";
import { supabase } from "../../lib/supabase";
import { fichaDesdeLaTienda, type ProductoEnLaTienda } from "./cambiosContraLaTienda";
import {
  actualizarFicha,
  actualizarVariante,
  agregarVariante,
  fetchCategorias,
  guardarBaseDeLaTienda,
  guardarSeccionesDestrabadas,
  marcarCategoriaDeProducto,
  quitarVariante,
  type CamposDeFicha,
  type CategoriaDeTienda,
} from "./lanzamientosApi";

const clave = (valores: readonly string[]) => valores.map((v) => (v ?? "").trim().toLowerCase()).join("\u0000");

/**
 * Deja la ficha (campos, variantes y categorías) igual a lo publicado. Las variantes se cruzan por sus
 * valores: la que ya estaba conserva su id (y sus fotos asignadas); las que la tienda no tiene se
 * quitan. Devuelve el primer error, o null.
 */
export async function traerLoDeLaTienda(
  producto: { id: string; variantes: ReadonlyArray<{ id: string; valores: string[] }>; categorias: readonly string[] },
  publicado: ProductoEnLaTienda,
  arbol: readonly CategoriaDeTienda[],
  companyId: string,
  /** Trabar todas las secciones (ver arriba). Sin esto, quedan como estaban. */
  opciones: { trabar?: boolean } = {},
): Promise<{ error: string | null }> {
  const f = fichaDesdeLaTienda(publicado, arbol);
  const r = await actualizarFicha(producto.id, f.campos as unknown as CamposDeFicha);
  if (r.error) return r;
  // La ficha arranca igual a la tienda: esto es con qué se compara después lo que tocó la persona
  // (ADR-2026-10-23-publicar-manda-solo-lo-que-tocaste).
  await guardarBaseDeLaTienda(producto.id, publicado);
  // Sin la columna falla sola: nada queda trabado, como antes.
  if (opciones.trabar) await guardarSeccionesDestrabadas(producto.id, []);

  const actuales = new Map(producto.variantes.map((v) => [clave(v.valores), v.id]));
  const quedan = new Set<string>();
  for (const [orden, v] of f.variantes.entries()) {
    const { valores, ...campos } = v;
    let id = actuales.get(clave(valores)) ?? null;
    if (!id) id = await agregarVariante(producto.id, companyId, valores, orden);
    if (!id) return { error: "No se pudo traer una variante de la tienda" };
    quedan.add(id);
    const rv = await actualizarVariante(id, {
      ...Object.fromEntries(Object.entries(campos).map(([k, x]) => [k, x ?? ""])),
      valores,
      orden,
    });
    if (rv.error) return rv;
  }
  for (const v of producto.variantes) {
    if (quedan.has(v.id)) continue;
    const rq = await quitarVariante(v.id);
    if (rq.error) return rq;
  }

  const tener = new Set(f.categoriaIds);
  for (const id of f.categoriaIds) {
    if (producto.categorias.includes(id)) continue;
    const rc = await marcarCategoriaDeProducto(producto.id, id, companyId, true);
    if (rc.error) return rc;
  }
  for (const id of producto.categorias) {
    if (tener.has(id)) continue;
    const rc = await marcarCategoriaDeProducto(producto.id, id, companyId, false);
    if (rc.error) return rc;
  }
  return { error: null };
}

/**
 * A los productos que se acaban de sumar al lanzamiento y ya están en la tienda, la ficha de la tienda.
 * Nunca tira y no frena el alta: si la tienda no se puede leer, quedan con los datos del catálogo y la
 * ficha ofrece "Traer lo de la tienda".
 */
export async function completarConLaTienda(
  lanzamientoId: string,
  companyId: string,
  lanzamientoProductoIds: string[],
): Promise<void> {
  if (lanzamientoProductoIds.length === 0) return;
  try {
    const [{ data: lanz }, { data: filas }] = await Promise.all([
      supabase.from("lanzamientos").select("brand_id, canal, destinos").eq("id", lanzamientoId).maybeSingle(),
      supabase
        .from("lanzamientos_productos")
        .select("id, productos(tn_product_id)")
        .in("id", lanzamientoProductoIds)
        .eq("id_company", companyId),
    ]);
    const destinos = ((lanz as { destinos?: string[] | null; canal?: string } | null)?.destinos ?? [
      (lanz as { canal?: string } | null)?.canal,
    ]) as Array<string | undefined>;
    if (!destinos.includes("tiendanube")) return;
    const trabar = destinos.every((d) => d === "tiendanube");
    type Rel = { tn_product_id: number | null } | null;
    const conTienda = ((filas || []) as unknown as Array<{ id: string; productos: Rel | Rel[] }>).flatMap((f) => {
      // La relación llega como objeto o como lista de uno, según cómo la tipe Supabase.
      const rel = Array.isArray(f.productos) ? (f.productos[0] ?? null) : f.productos;
      return rel?.tn_product_id != null ? [{ id: f.id, tn: Number(rel.tn_product_id) }] : [];
    });
    if (conTienda.length === 0) return;
    const brandId = (lanz as { brand_id?: string | null } | null)?.brand_id ?? null;
    const [publicados, arbol] = await Promise.all([
      leerPublicadosEnTn(brandId, conTienda.map((x) => x.tn)),
      fetchCategorias(companyId, brandId),
    ]);
    if (!publicados) return;
    for (const x of conTienda) {
      const p = publicados.get(x.tn);
      // Filas recién creadas: sin variantes ni categorías todavía.
      if (p) await traerLoDeLaTienda({ id: x.id, variantes: [], categorias: [] }, p, arbol, companyId, { trabar });
    }
  } catch (err) {
    console.error("[traerDeLaTienda] completar con la tienda", err);
  }
}

/** Lo que se mandó de una ficha al sincronizar: con esto se sabe qué limpiar después y si se la tocó mientras tanto. */
export interface FichaEnviada {
  imagenes: ReadonlyArray<{ id: string }>;
  fotosABorrar?: readonly number[] | null;
  /** `firmaDeLaFicha` de lo que se mandó. */
  firma: string;
}

/**
 * Lo que la persona puede editar de una ficha, en un texto: si cambia, la tocaron. Es TODO menos lo que cambia
 * solo (estado, ids de la tienda, fotos, la base que se compara): una lista de campos incluidos se quedaba corta
 * cada vez que la ficha sumaba uno (review Greptile de #232).
 */
const NO_SON_EDICION = new Set([
  "id", "estado", "error", "tnProductId", "imagenes", "fotosABorrar", "tiendaBase", "seccionesDestrabadas", "porDestino",
  "orden", "productoId", "productos_", "updatedAt", "creadoEn",
]);
export function firmaDeLaFicha(ficha: object): string {
  const limpio = (o: Record<string, unknown>): unknown[] =>
    Object.keys(o)
      .filter((k) => !NO_SON_EDICION.has(k) && !/^tn[A-Z]/.test(k))
      .sort()
      .map((k): unknown => [k, k === "variantes" && Array.isArray(o[k]) ? (o[k] as Record<string, unknown>[]).map(limpio) : o[k] ?? null]);
  return JSON.stringify(limpio(ficha as Record<string, unknown>));
}

export function enviadaDe(p: { imagenes: ReadonlyArray<{ id: string }>; fotosABorrar?: number[] | null }): FichaEnviada {
  return { imagenes: p.imagenes.map((i) => ({ id: i.id })), fotosABorrar: p.fotosABorrar ?? [], firma: firmaDeLaFicha(p) };
}

/**
 * Después de sincronizar desde Tienda online (ADR-2026-10-29-fotos-de-la-tienda-se-borran-una-por-una): la
 * ficha de cada producto que quedó publicado vuelve a ser la tienda. Las fotos nuevas ya están allá (si
 * siguieran en la ficha se verían dos veces y se volverían a mandar), las borradas ya no están, y lo que
 * cambió es lo de la tienda ahora: la base se rehace y las secciones se vuelven a trabar.
 */
export async function reiniciarConLaTienda(
  productos: ReadonlyArray<
    { id: string; tnProductId: number | null; variantes: ReadonlyArray<{ id: string; valores: string[] }>; categorias: readonly string[]; fotosABorrar?: number[] | null }
  >,
  brandId: string | null,
  arbol: readonly CategoriaDeTienda[],
  companyId: string,
  /**
   * Lo que se mandó de cada una. Se limpia sólo eso: una foto sumada o un campo cambiado mientras corría la
   * sincronización no se mandó y no se borra (review Greptile de #232). Una ficha que no está acá no se toca.
   */
  enviadas: ReadonlyMap<string, FichaEnviada>,
  /**
   * Relee las fichas justo antes de decidir: entre la lectura de quien llama y acá está la de Tiendanube, y una
   * edición hecha en ese rato también cuenta.
   */
  releer?: () => Promise<ReadonlyArray<{ id: string } & object> | null>,
): Promise<void> {
  const conTienda = productos.filter((p) => p.tnProductId != null);
  if (conTienda.length === 0) return;
  try {
    const publicados = await leerPublicadosEnTn(
      brandId,
      conTienda.map((p) => p.tnProductId!),
    );
    if (!publicados) return;
    const frescas = new Map(((releer ? await releer().catch(() => null) : null) ?? []).map((x) => [x.id, x]));
    for (const p of conTienda) {
      const publicado = publicados.get(p.tnProductId!);
      if (!publicado) continue;
      const enviada = enviadas.get(p.id);
      if (!enviada) continue;
      const ids = enviada.imagenes.map((i) => i.id);
      if (ids.length > 0) {
        await supabase.from("lanzamientos_imagenes").delete().in("id", ids).eq("lanzamiento_producto_id", p.id).eq("id_company", companyId);
      }
      const mandadas = new Set(enviada.fotosABorrar ?? []);
      const ahora = (frescas.get(p.id) as { fotosABorrar?: number[] | null } | undefined) ?? p;
      const quedan = (ahora.fotosABorrar ?? []).filter((id) => !mandadas.has(id));
      await supabase.from("lanzamientos_productos").update({ fotos_a_borrar: quedan }).eq("id", p.id);
      // Si la tocaron mientras tanto, lo nuevo queda como cambio pendiente: traer la tienda lo pisaría.
      if (firmaDeLaFicha(frescas.get(p.id) ?? p) === enviada.firma) await traerLoDeLaTienda(p, publicado, arbol, companyId, { trabar: true });
    }
  } catch (err) {
    console.error("[traerDeLaTienda] reiniciar después de sincronizar", err);
  }
}
